import React, { useCallback, useEffect, useState } from "react";
import { IoMdArrowRoundBack, IoMdTrash } from "react-icons/io";
import Swal from "sweetalert2";
import moment from "moment";

import CheckboxInput from "../../components/CheckboxInput";
import Select from "../../components/Select";
import Spinner from "../../components/Spinner";
import VentasReportePanel from "../../components/VentasReportePanel";
import useAuthFetch from "../../hooks/useAuthFetch";

const SWAL_THEME = {
  background: "#76236C",
  color: "#fafafa",
  confirmButtonColor: "#19D3C5",
};

export function DescuentosMasivosPage() {
  const authFetch = useAuthFetch();

  const [tab, setTab] = useState("descuentos");
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [productCount, setProductCount] = useState(null);
  const [error, setError] = useState("");

  const [descuento, setDescuento] = useState({
    porcentaje: "", validoDesde: "", validoHasta: "",
    cantidadDisponible: 0, soloEnEfectivo: false, activo: true,
  });
  const [discountForAll, setDiscountForAll] = useState(false);
  const [removing, setRemoving] = useState(false);

  const [activeDiscounts, setActiveDiscounts] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [filterBrand, setFilterBrand] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const fetchActiveDiscounts = useCallback(async () => {
    setLoadingDiscounts(true);
    try {
      const filterObj = {
        where: { activo: 1 },
        include: [{ relation: "descuentos" }],
        fields: { id: true, nombreTienda: true, idMarca: true, marca: true },
        order: ["marca asc", "nombreTienda asc"],
      };
      const res = await authFetch(`producto-detalles?filter=${JSON.stringify(filterObj)}`);
      const data = await res.json();
      const now = new Date();
      setActiveDiscounts(data.filter(
        (p) => p.descuentos?.some((d) => d.activo && new Date(d.validoDesde) <= now && new Date(d.validaHasta) >= now)
      ));
    } catch { /* silently fail */ }
    setLoadingDiscounts(false);
  }, [authFetch]);

  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      const res = await authFetch(`marcas?filter={"where":{"activo":1},"order":["nombreTienda asc"]}`);
      setBrands(await res.json());
      setLoading(false);
    };
    fetchBrands();
    fetchActiveDiscounts();
  }, []);

  const fetchProductCount = async (idMarca) => {
    const res = await authFetch(`productos/count?where=${JSON.stringify({ idMarca, activo: 1 })}`);
    const data = await res.json();
    setProductCount(data.count ?? 0);
  };

  const handleBrandChange = (idMarca) => {
    setSelectedBrand(idMarca); setProductCount(null);
    if (idMarca) fetchProductCount(idMarca);
  };

  const validate = () => {
    if (!selectedBrand) return "Seleccioná una marca.";
    if (!descuento.porcentaje || Number(descuento.porcentaje) <= 0) return "El porcentaje debe ser mayor a 0.";
    if (!descuento.validoDesde || !descuento.validoHasta) return "Completá las fechas de validez.";
    if (!discountForAll && (descuento.cantidadDisponible === "" || Number(descuento.cantidadDisponible) < 0))
      return "La cantidad disponible debe ser 0 o mayor.";
    return null;
  };

  const handleApply = async () => {
    setError("");
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    const brandName = brands.find((b) => b.id === selectedBrand)?.nombreTienda || "";
    const confirmed = await Swal.fire({
      title: `¿Aplicar descuento a ${brandName}?`,
      html: `Se aplicará un <strong>${descuento.porcentaje}%</strong> de descuento sobre el precio de lista (sin IVA) a los <strong>${productCount}</strong> productos activos de esta marca.<br/><br/>Se eliminarán los descuentos existentes de estos productos.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, aplicar",
      cancelButtonText: "Cancelar", cancelButtonColor: "#E7004C", ...SWAL_THEME,
    });
    if (!confirmed.isConfirmed) return;
    setLoading(true);
    try {
      const res = await authFetch("producto-descuentos/masivo", {
        idMarca: selectedBrand, porcentaje: Number(descuento.porcentaje) / 100,
        validoDesde: descuento.validoDesde, validoHasta: descuento.validoHasta,
        cantidadDisponible: discountForAll ? null : Number(descuento.cantidadDisponible),
        soloEnEfectivo: descuento.soloEnEfectivo, activo: true,
      }, "POST");
      if (res.status === 200 || res.status === 201) {
        const data = await res.json();
        Swal.fire({ icon: "success", title: "¡Descuento aplicado!", text: `Se aplicó el descuento a ${data.productosAfectados} productos de ${brandName}.`, ...SWAL_THEME });
        setDescuento({ porcentaje: "", validoDesde: "", validoHasta: "", cantidadDisponible: 0, soloEnEfectivo: false, activo: true });
        setDiscountForAll(false); setSelectedBrand(null); setProductCount(null);
        fetchActiveDiscounts();
      } else { setError("Ocurrió un error al aplicar el descuento. Intentá de nuevo."); }
    } catch { setError("Ocurrió un error al aplicar el descuento. Intentá de nuevo."); }
    setLoading(false);
  };

  const handleRemoveAll = async () => {
    if (!selectedBrand) { setError("Seleccioná una marca primero."); return; }
    const brandName = brands.find((b) => b.id === selectedBrand)?.nombreTienda || "";
    const confirmed = await Swal.fire({
      title: `¿Eliminar descuentos de ${brandName}?`,
      html: `Se eliminarán todos los descuentos activos de los <strong>${productCount}</strong> productos activos de esta marca.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar", confirmButtonColor: "#E7004C", cancelButtonColor: "#19D3C5", ...SWAL_THEME,
    });
    if (!confirmed.isConfirmed) return;
    setRemoving(true);
    try {
      const res = await authFetch("producto-descuentos/masivo", {
        idMarca: selectedBrand, porcentaje: 0,
        validoDesde: new Date().toISOString(), validoHasta: new Date().toISOString(),
        cantidadDisponible: null, soloEnEfectivo: false, activo: false,
      }, "POST");
      if (res.status === 200 || res.status === 201) {
        const data = await res.json();
        Swal.fire({ icon: "success", title: "¡Descuentos eliminados!", text: `Se eliminaron los descuentos de ${data.productosAfectados} productos de ${brandName}.`, ...SWAL_THEME });
        setProductCount(null); fetchProductCount(selectedBrand); fetchActiveDiscounts();
      } else { setError("Ocurrió un error al eliminar los descuentos."); }
    } catch { setError("Ocurrió un error al eliminar los descuentos."); }
    setRemoving(false);
  };

  const handleCancelOne = async (product, descuentoId) => {
    const confirmed = await Swal.fire({
      title: "¿Cancelar este descuento?", html: `Se eliminará el descuento de <strong>${product.nombreTienda}</strong>.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No", confirmButtonColor: "#E7004C", cancelButtonColor: "#19D3C5", ...SWAL_THEME,
    });
    if (!confirmed.isConfirmed) return;
    setCancellingId(descuentoId);
    try {
      const res = await authFetch(`producto-descuentos/${descuentoId}`, {}, "DELETE");
      if (res.status === 200 || res.status === 204) { fetchActiveDiscounts(); }
      else { Swal.fire({ icon: "error", title: "Error", text: "No se pudo cancelar el descuento.", ...SWAL_THEME }); }
    } catch { Swal.fire({ icon: "error", title: "Error", text: "No se pudo cancelar el descuento.", ...SWAL_THEME }); }
    setCancellingId(null);
  };

  const handleCancelBrandFromList = async (idMarca, brandName, brandProducts) => {
    const confirmed = await Swal.fire({
      title: `¿Eliminar todos los descuentos de ${brandName}?`,
      html: `Se eliminarán los descuentos de <strong>${brandProducts.length}</strong> productos.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar", confirmButtonColor: "#E7004C", cancelButtonColor: "#19D3C5", ...SWAL_THEME,
    });
    if (!confirmed.isConfirmed) return;
    setLoadingDiscounts(true);
    try {
      const res = await authFetch("producto-descuentos/masivo", {
        idMarca, porcentaje: 0, validoDesde: new Date().toISOString(), validoHasta: new Date().toISOString(),
        cantidadDisponible: null, soloEnEfectivo: false, activo: false,
      }, "POST");
      if (res.status === 200 || res.status === 201) { fetchActiveDiscounts(); }
      else { Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar.", ...SWAL_THEME }); }
    } catch { Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar.", ...SWAL_THEME }); }
    setLoadingDiscounts(false);
  };

  const discountsByBrand = activeDiscounts.reduce((acc, product) => {
    const key = product.marca || "Sin marca";
    if (!acc[key]) acc[key] = { idMarca: product.idMarca, products: [] };
    acc[key].products.push(product);
    return acc;
  }, {});

  const filteredBrandKeys = filterBrand
    ? Object.keys(discountsByBrand).filter((k) => discountsByBrand[k].idMarca === filterBrand)
    : Object.keys(discountsByBrand).sort();

  const uniqueBrandsInList = Object.entries(discountsByBrand).map(([name, { idMarca }]) => ({ id: idMarca, nombre: name }));

  const TABS = [
    { key: "descuentos", label: "Descuentos masivos" },
    { key: "reporte", label: "Reporte de ventas" },
  ];

  return (
    <div className="w-full p-content pb-16 min-h-[400px]">
      {loading && !brands.length ? <Spinner /> : (
        <div>
          {/* Header */}
          <div className="flex w-full justify-end items-center gap-4 mb-6">
            <button className="bg-pink text-white py-2 px-8 font-bold rounded-full flex justify-center items-center"
              onClick={() => (window.location.href = "/inventario")}>
              <IoMdArrowRoundBack className="text-lg mr-1" /> Volver
            </button>
          </div>

          <div className="flex border-b border-gray-200 mb-8">
            {TABS.map((t) => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.key
                    ? "border-purple text-purple"
                    : "border-transparent text-gray-500 hover:text-purple"
                  }`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "descuentos" && (
          <div>
              <p className="text-2xl font-bold mb-2">Descuentos masivos por marca</p>
              <p className="text-sm text-gray-500 mb-6">
                Aplicá un descuento a todos los productos activos de una marca de una sola vez. El porcentaje se aplica sobre el{" "}
                <strong>precio de lista sin IVA</strong>.
              </p>

              <div className="max-w-xl">
                <Select containerClasses="my-2" labelColor="text-base" buttonClasses="border border-purple"
                  label="Marca:" required
                  options={brands.map((b) => ({ value: b.id, label: b.nombreTienda }))}
                  onChange={handleBrandChange} value={selectedBrand} />

                {selectedBrand && productCount !== null && (
                  <div className="my-3 p-3 bg-purple bg-opacity-10 rounded-xl border border-purple text-sm">
                    Esta marca tiene <strong>{productCount}</strong> producto(s) activo(s).
                    {productCount === 0 && <span className="text-pink ml-1">No hay productos para aplicar descuento.</span>}
                  </div>
                )}

                <hr className="my-4" />

                <div className="flex flex-col gap-4">
                  <div>
                    <label className="text-base">Descuento en porcentaje sobre precio de lista sin IVA (Ej: 12):<span className="text-pink">*</span></label>
                    <input type="number" className="w-full rounded-3xl my-2 h-12 p-6 border border-purple"
                      min={1} max={100} value={descuento.porcentaje} placeholder="Ej: 12"
                      onChange={(e) => setDescuento({ ...descuento, porcentaje: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-base">Válido desde:<span className="text-pink">*</span></label>
                    <input type="date" className="w-full rounded-3xl my-2 h-12 p-6 border border-purple"
                      value={descuento.validoDesde}
                      onChange={(e) => setDescuento({ ...descuento, validoDesde: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-base">Válido hasta:<span className="text-pink">*</span></label>
                    <input type="date" className="w-full rounded-3xl my-2 h-12 p-6 border border-purple"
                      value={descuento.validoHasta}
                      onChange={(e) => setDescuento({ ...descuento, validoHasta: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-base">Cantidad disponible con descuento:</label>
                    <input type="number" disabled={discountForAll}
                      className="w-full rounded-3xl my-2 h-12 p-6 border border-purple disabled:opacity-50"
                      value={discountForAll ? "" : descuento.cantidadDisponible}
                      placeholder="Dejar en 0 para ninguna limitación"
                      onChange={(e) => setDescuento({ ...descuento, cantidadDisponible: e.target.value })} />
                  </div>
                  <div className="flex gap-2 items-center">
                    <label>Aplicar descuento a todas las existencias (sin límite)</label>
                    <CheckboxInput checked={discountForAll} onCheckChange={() => setDiscountForAll(!discountForAll)} />
                  </div>
                  <div className="flex gap-2 items-center">
                    <label>Solo con efectivo</label>
                    <CheckboxInput checked={descuento.soloEnEfectivo}
                      onCheckChange={() => setDescuento({ ...descuento, soloEnEfectivo: !descuento.soloEnEfectivo })} />
                  </div>
                </div>

                {error && <p className="text-pink text-center mt-4">{error}</p>}

                <div className="flex flex-col gap-3 mt-6">
                  <button onClick={handleApply} disabled={!selectedBrand || productCount === 0}
                    className="bg-purple text-white h-12 px-8 rounded-full w-full disabled:opacity-50">
                    Aplicar descuento a toda la marca
                  </button>
                  <button onClick={handleRemoveAll} disabled={!selectedBrand || productCount === 0 || removing}
                    className="bg-pink text-white h-12 px-8 rounded-full w-full disabled:opacity-50">
                    {removing ? "Eliminando..." : "Eliminar todos los descuentos de la marca seleccionada"}
                  </button>
                </div>
              </div>

              {/* Active discounts list */}
              <div className="mt-12">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <p className="text-xl font-bold">Descuentos activos</p>
                  <div className="flex items-center gap-3">
                    {uniqueBrandsInList.length > 0 && (
                      <Select containerClasses="min-w-[220px]" labelColor="text-sm" buttonClasses="border border-purple"
                        label="Filtrar por marca:"
                        options={[{ value: null, label: "Todas" }, ...uniqueBrandsInList.map((b) => ({ value: b.id, label: b.nombre }))]}
                        onChange={(v) => setFilterBrand(v || null)} value={filterBrand} />
                    )}
                    <button onClick={fetchActiveDiscounts} className="bg-purple text-white py-2 px-5 rounded-full text-sm">Actualizar</button>
                  </div>
                </div>

                {loadingDiscounts ? <Spinner /> : filteredBrandKeys.length === 0 ? (
                  <p className="text-gray-400 text-sm">No hay descuentos activos en este momento.</p>
                ) : (
                  <div className="flex flex-col gap-6">
                    {filteredBrandKeys.map((brandName) => {
                      const { idMarca, products } = discountsByBrand[brandName];
                      return (
                        <div key={brandName}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-bold text-lg">{brandName}</p>
                            <button onClick={() => handleCancelBrandFromList(idMarca, brandName, products)}
                              className="text-xs bg-pink text-white py-1 px-4 rounded-full hover:opacity-80">
                              Cancelar todos ({products.length})
                            </button>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                              <thead>
                                <tr className="bg-purple text-white text-left">
                                  <th className="p-3 rounded-tl-xl">Producto</th>
                                  <th className="p-3">Descuento</th>
                                  <th className="p-3">Válido desde</th>
                                  <th className="p-3">Válido hasta</th>
                                  <th className="p-3">Unidades</th>
                                  <th className="p-3 rounded-tr-xl">Cancelar</th>
                                </tr>
                              </thead>
                              <tbody>
                                {products.map((product) => {
                                  const d = product.descuentos.find((x) => x.activo && new Date(x.validoDesde) <= new Date() && new Date(x.validaHasta) >= new Date());
                                  if (!d) return null;
                                  return (
                                    <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50">
                                      <td className="p-3">{product.nombreTienda}</td>
                                      <td className="p-3">
                                        <span className="font-semibold text-green-600">{(d.porcentaje * 100).toFixed(0)}%</span>
                                        {d.soloEnEfectivo && <span className="ml-1 text-xs text-gray-400">(efectivo)</span>}
                                      </td>
                                      <td className="p-3">{moment(d.validoDesde).format("DD/MM/YYYY")}</td>
                                      <td className="p-3">{moment(d.validaHasta).format("DD/MM/YYYY")}</td>
                                      <td className="p-3">{d.cantidadDisponible === null ? "Ilimitado" : d.cantidadDisponible}</td>
                                      <td className="p-3">
                                        <button disabled={cancellingId === d.id} onClick={() => handleCancelOne(product, d.id)}
                                          className="p-2 rounded-full bg-pink text-white hover:opacity-80 disabled:opacity-40" title="Cancelar descuento">
                                          <IoMdTrash className="text-base" />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "reporte" && (
            <div>
              <p className="text-2xl font-bold mb-2">Reporte de Ventas Promocionales</p>
              <VentasReportePanel
                reportBase="reportes/ventas-promocionales"
                description="Descargá un archivo Excel con las ventas de productos que tuvieron descuento activo en el período seleccionado."
                emptyMessage="No hay ventas promocionales en el período seleccionado."
                downloadFilenamePrefix="reporte-ventas-promocionales"
                previewCountSuffix={{ singular: "línea de producto con descuento", plural: "líneas de producto con descuento" }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DescuentosMasivosPage;
